import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";

function useTrend(endpoint) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!endpoint) { setData(null); setMeta(null); setError(null); return; }
    const base = process.env.REACT_APP_API_URL || "";
    fetch(`${base}${endpoint}`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setData(d.data || []);
        setMeta({ totals: d.totals, days: d.days, date: d.date });
      })
      .catch((e) => setError(e.message));
  }, [endpoint]);

  return { data, meta, error };
}

function ChartPanel({ title, data, error, children }) {
  if (error) return <div className="panel"><div className="panel-header">{title}</div><div className="panel-body">Error: {error}</div></div>;
  if (!data) return <div className="panel"><div className="panel-header">{title}</div><div className="panel-body">Loading...</div></div>;
  return (
    <div className="panel">
      <div className="panel-header">{title}</div>
      <div className="panel-body">{children}</div>
    </div>
  );
}

export default function TrendsPanel() {
  const [activityDays, setActivityDays] = useState(30);
  const [drillDate, setDrillDate] = useState(null);
  const activity = useTrend(`/api/trends/recent-activity?days=${activityDays}`);
  const hourly = useTrend(drillDate ? `/api/trends/hourly-activity?date=${drillDate}` : null);
  const cves = useTrend("/api/trends/by-year");
  const cnas = useTrend("/api/trends/cnas-by-year");
  const roles = useTrend("/api/trends/cna-roles-by-year");

  const cveTotal = (cves.data || []).reduce((s, d) => s + d.count, 0);
  const cnaMax = (cnas.data || []).reduce((m, d) => Math.max(m, d.unique_cnas), 0);
  const latestRole = (roles.data || []).slice(-1)[0];
  const activityTotals = activity.meta?.totals || { published: 0, modified: 0, scored: 0 };
  const hourlyTotals = hourly.meta?.totals || { published: 0, modified: 0, scored: 0 };

  const title = drillDate
    ? `Hourly Activity — ${drillDate} (cvelistV5)`
    : "Recent CVE Activity (cvelistV5)";
  const chartData = drillDate ? (hourly.data || []) : (activity.data || []);
  const chartError = drillDate ? hourly.error : activity.error;
  const chartDataReady = drillDate ? hourly.data : activity.data;
  const xKey = drillDate ? "hour" : "date";
  const totals = drillDate ? hourlyTotals : activityTotals;
  const subtitle = drillDate
    ? `24 hours (ET) · `
    : `Last ${activityDays} days · `;

  return (
    <>
      <ChartPanel title={title} data={chartDataReady} error={chartError}>
        {chartDataReady && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
              <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                {subtitle}
                <span style={{ color: "#58a6ff" }}>{totals.published.toLocaleString()} published</span> · <span style={{ color: "#f0883e" }}>{totals.modified.toLocaleString()} modified</span> · <span style={{ color: "#a371f7" }}>{totals.scored.toLocaleString()} scored by Vinnie</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {drillDate && (
                  <>
                    <button
                      onClick={() => {
                        const d = new Date(drillDate + "T12:00:00");
                        d.setDate(d.getDate() - 1);
                        setDrillDate(d.toISOString().slice(0, 10));
                      }}
                      style={{
                        background: "var(--bg-secondary)", color: "var(--text-primary)",
                        border: "1px solid var(--border)", borderRadius: 4,
                        padding: "4px 8px", fontSize: 12, cursor: "pointer",
                      }}
                      title="Previous day"
                    >‹</button>
                    <input
                      type="date"
                      value={drillDate}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => { if (e.target.value) setDrillDate(e.target.value); }}
                      style={{
                        background: "var(--bg-secondary)", color: "var(--text-primary)",
                        border: "1px solid var(--border)", borderRadius: 4,
                        padding: "4px 8px", fontSize: 12,
                        colorScheme: "dark",
                      }}
                    />
                    <button
                      onClick={() => {
                        const d = new Date(drillDate + "T12:00:00");
                        d.setDate(d.getDate() + 1);
                        const next = d.toISOString().slice(0, 10);
                        const today = new Date().toISOString().slice(0, 10);
                        if (next <= today) setDrillDate(next);
                      }}
                      disabled={drillDate >= new Date().toISOString().slice(0, 10)}
                      style={{
                        background: "var(--bg-secondary)", color: "var(--text-primary)",
                        border: "1px solid var(--border)", borderRadius: 4,
                        padding: "4px 8px", fontSize: 12,
                        cursor: drillDate >= new Date().toISOString().slice(0, 10) ? "not-allowed" : "pointer",
                        opacity: drillDate >= new Date().toISOString().slice(0, 10) ? 0.4 : 1,
                      }}
                      title="Next day"
                    >›</button>
                    <button
                      onClick={() => setDrillDate(null)}
                      style={{
                        background: "var(--bg-secondary)", color: "var(--text-primary)",
                        border: "1px solid var(--border)", borderRadius: 4,
                        padding: "4px 10px", fontSize: 12, cursor: "pointer",
                      }}
                    >
                      ← Back to daily
                    </button>
                  </>
                )}
                {!drillDate && (
                  <select
                    value={activityDays}
                    onChange={(e) => setActivityDays(parseInt(e.target.value, 10))}
                    style={{
                      background: "var(--bg-secondary)", color: "var(--text-primary)",
                      border: "1px solid var(--border)", borderRadius: 4,
                      padding: "4px 8px", fontSize: 12,
                    }}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>365 days</option>
                  </select>
                )}
              </div>
            </div>
            {!drillDate && (
              <div style={{ color: "var(--text-secondary)", fontSize: 11, marginBottom: 8, opacity: 0.7 }}>
                Tip: click any day to drill into hourly activity.
              </div>
            )}
            <ResponsiveContainer width="100%" height={420}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 10, bottom: 50 }}
                onClick={(state) => {
                  if (drillDate) return;
                  // BarChart onClick callback: state.activeLabel is the X value
                  const label = state?.activeLabel;
                  if (label && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
                    setDrillDate(label);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey={xKey}
                  tick={{ fill: "var(--text-secondary)", fontSize: 10, cursor: drillDate ? "default" : "pointer" }}
                  angle={-45} textAnchor="end" height={60}
                  interval={drillDate ? 0 : (activityDays > 60 ? Math.floor(activityDays / 30) : "preserveStartEnd")}
                  onClick={(e) => {
                    if (drillDate) return;
                    const label = e?.value;
                    if (label && /^\d{4}-\d{2}-\d{2}$/.test(label)) setDrillDate(label);
                  }}
                />
                <YAxis
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <Tooltip
                  cursor={!drillDate ? { fill: "rgba(255,255,255,0.04)", cursor: "pointer" } : { fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                  }}
                  formatter={(v, name) => [v.toLocaleString(), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="published"
                  fill="#58a6ff"
                  radius={[3, 3, 0, 0]}
                  name="Published"
                  cursor={!drillDate ? "pointer" : undefined}
                  onClick={(d) => { if (!drillDate && d?.date) setDrillDate(d.date); }}
                />
                <Bar
                  dataKey="modified"
                  fill="#f0883e"
                  radius={[3, 3, 0, 0]}
                  name="Modified"
                  cursor={!drillDate ? "pointer" : undefined}
                  onClick={(d) => { if (!drillDate && d?.date) setDrillDate(d.date); }}
                />
                <Bar
                  dataKey="scored"
                  fill="#a371f7"
                  radius={[3, 3, 0, 0]}
                  name="Scored by Vinnie"
                  cursor={!drillDate ? "pointer" : undefined}
                  onClick={(d) => { if (!drillDate && d?.date) setDrillDate(d.date); }}
                />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </ChartPanel>

      <ChartPanel title="CVEs by Year" data={cves.data} error={cves.error}>
        {cves.data && (
          <>
            <div style={{ marginBottom: 12, color: "var(--text-secondary)", fontSize: 13 }}>
              {cveTotal.toLocaleString()} total CVEs across {cves.data.length} years
            </div>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={cves.data} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="year"
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  angle={-45} textAnchor="end" height={50} interval={0}
                />
                <YAxis
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                  }}
                  formatter={(v) => [v.toLocaleString(), "CVEs"]}
                />
                <Bar dataKey="count" fill="#58a6ff" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </ChartPanel>

      <ChartPanel title="CVEs by CNA Role (Vendor vs Third-Party)" data={roles.data} error={roles.error}>
        {roles.data && (
          <>
            <div style={{ marginBottom: 12, color: "var(--text-secondary)", fontSize: 13 }}>
              {latestRole && `${latestRole.year}: ${latestRole.supplier_pct}% assigned by the product's vendor (Supplier), ${(100 - latestRole.supplier_pct).toFixed(1)}% by third parties (researchers/coordinators)`}
            </div>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={roles.data} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="year"
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  angle={-45} textAnchor="end" height={50} interval={0}
                />
                <YAxis
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                  }}
                  formatter={(v, _name, p) => [v.toLocaleString(), p.dataKey === "supplier" ? "Vendor (Supplier)" : "Third-Party"]}
                />
                <Bar dataKey="supplier" stackId="a" fill="#3fb950" radius={[0, 0, 0, 0]} name="Vendor (Supplier)" />
                <Bar dataKey="third_party" stackId="a" fill="#f0883e" radius={[3, 3, 0, 0]} name="Third-Party" />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </ChartPanel>

      <ChartPanel title="Unique CNAs by Year" data={cnas.data} error={cnas.error}>
        {cnas.data && (
          <>
            <div style={{ marginBottom: 12, color: "var(--text-secondary)", fontSize: 13 }}>
              Peak: {cnaMax} CNAs in a single year
            </div>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={cnas.data} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="year"
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  angle={-45} textAnchor="end" height={50} interval={0}
                />
                <YAxis
                  tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                  }}
                  formatter={(v, _, p) => [`${v} CNAs (${p.payload.total_cves.toLocaleString()} CVEs)`, "Unique CNAs"]}
                />
                <Bar dataKey="unique_cnas" fill="#3fb950" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </ChartPanel>
    </>
  );
}
